import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import RequestDetail from './RequestDetail';
import { Loader2, ArrowLeft, CheckCircle, Bell, Search } from 'lucide-react';
import { formatLongDate, getServerTime } from '../utils/datetime';

const collectText = (value, out) => {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) { value.forEach(v => collectText(v, out)); return; }
  if (typeof value === 'object') { Object.values(value).forEach(v => collectText(v, out)); return; }
  out.push(String(value));
};

const buildSearchText = (req) => {
  const parts = [
    req.id,
    req.formName,
    req.initiatorName,
    req.status,
    req.currentNodeLabel,
    formatLongDate(req.createdAt),
    req.myLastActionAt ? formatLongDate(req.myLastActionAt) : ''
  ];
  let data = req.formData;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) { }
  }
  collectText(data, parts);
  return parts.filter(Boolean).join(' ').toLowerCase();
};

const activityTime = (req) => {
  for (const value of [req.myLastActionAt, req.updatedAt, req.createdAt]) {
    const t = getServerTime(value);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
};

const wasApprovedByMe = (req) =>
  req.status === 'approved' ||
  (Array.isArray(req.approvedNodeIds) && req.approvedNodeIds.length > 0);

export default function EmployeeApprovals({ user }) {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [linkNotice, setLinkNotice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/pending-approvals`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
        const pendingRequestId = localStorage.getItem('approvalRequestId');
        if (pendingRequestId) {
          // One shot only - the key is cleared either way so a stale id cannot hijack a later visit.
          localStorage.removeItem('approvalRequestId');
          const pendingMatch = data.find(r => String(r.id) === String(pendingRequestId));
          if (pendingMatch) {
            setSelectedRequest(pendingMatch);
            if (pendingMatch.status !== 'pending') setFilterStatus('all');
          } else {
            setLinkNotice(`Request ${pendingRequestId} is no longer available to you. It may have been completed, reassigned, or removed.`);
          }
        } else if (selectedRequest) {
          const updated = data.find(r => r.id === selectedRequest.id);
          if (updated) setSelectedRequest(updated);
          else setSelectedRequest(null);
        }
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const indexedRequests = useMemo(() => requests
    .map(req => ({ req, text: buildSearchText(req), time: activityTime(req) }))
    .sort((a, b) => b.time - a.time), [requests]);

  const searchTokens = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const filteredRequests = indexedRequests
    .filter(({ req, text }) =>
      (filterStatus === 'all' ||
        (filterStatus === 'pending'
          ? req.status === 'pending' && req.isAwaitingMyApproval
          : filterStatus === 'approved'
            ? wasApprovedByMe(req)
          : req.status === filterStatus)) &&
      searchTokens.every(token => text.includes(token))
    )
    .map(({ req }) => req);

  const getStatusCount = (status) => {
    if (status === 'all') return requests.length;
    if (status === 'pending') {
      return requests.filter(r => r.status === 'pending' && r.isAwaitingMyApproval).length;
    }
    if (status === 'approved') {
      return requests.filter(wasApprovedByMe).length;
    }
    return requests.filter(r => r.status === status).length;
  };

  const pendingCount = requests.filter(r => r.status === 'pending' && r.isAwaitingMyApproval).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="submissions-view">
      <div className="submissions-header">
        <div>
          <h1 className="submissions-title">
            Pending Approvals
            {pendingCount > 0 && (
              <span style={{
                background: '#dc2626',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '16px',
                marginLeft: '12px'
              }}>
                {pendingCount}
              </span>
            )}
          </h1>
          <p className="dashboard-subtitle">Review and approve/reject requests assigned to you</p>
        </div>
      </div>

      {linkNotice && (
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          padding: '14px 16px',
          marginBottom: '16px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '8px'
        }}>
          <Bell size={20} style={{ color: '#92400e', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>Nothing to open</div>
            <div style={{ fontSize: '13px', color: '#b45309', marginTop: '4px' }}>{linkNotice}</div>
          </div>
          <button onClick={() => setLinkNotice(null)} className="button-link" style={{ color: '#92400e', cursor: 'pointer' }}>Dismiss</button>
        </div>
      )}

      <div className="dashboard-filters">
        <div className="filter-buttons">
          <button
            onClick={() => setFilterStatus('all')}
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
          >
            All ({getStatusCount('all')})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
          >
            Pending ({getStatusCount('pending')})
          </button>
          <button
            onClick={() => setFilterStatus('approved')}
            className={`filter-btn ${filterStatus === 'approved' ? 'active' : ''}`}
          >
            Approved ({getStatusCount('approved')})
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`filter-btn ${filterStatus === 'rejected' ? 'active' : ''}`}
          >
            Rejected ({getStatusCount('rejected')})
          </button>
        </div>
        <div className="request-search-wrap" style={{ flex: 1 }}>
          <Search size={16} className="request-search-icon" />
          <input
            type="text"
            className="request-search-input"
            placeholder="Search form, initiator, stage or any field value..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        searchTokens.length > 0 ? (
          <div className="empty-state">
            <Search size={48} />
            <h3>No matches</h3>
            <p>No requests match "{searchTerm.trim()}"</p>
          </div>
        ) : (
          <div className="empty-state">
            <CheckCircle size={48} />
            <h3>No approvals pending</h3>
            <p>You don't have any requests awaiting your approval</p>
          </div>
        )
      ) : (
        <div className="submissions-content">
          <div className="submissions-list">
            {filteredRequests.map(request => (
              <div
                key={request.id}
                className={`submission-item ${selectedRequest?.id === request.id ? 'active' : ''}`}
                onClick={() => setSelectedRequest(request)}
                style={request.isAwaitingMyApproval ? { borderLeft: '4px solid #dc2626' } : {}}
              >
                <div className="submission-item-header">
                  <span className={`status-badge status-${request.status}`}>
                    {request.status}
                    {request.isAwaitingMyApproval && <Bell size={14} style={{ marginLeft: '4px' }} />}
                  </span>
                  <span className="submission-date">
                    {formatLongDate(request.createdAt)}
                  </span>
                </div>
                <div className="submission-item-preview">
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Form:</span>
                    <span className="preview-value-sm">{request.formName}</span>
                  </div>
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Initiator:</span>
                    <span className="preview-value-sm">{request.initiatorName}</span>
                  </div>
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Current:</span>
                    <span className="preview-value-sm">{request.currentNodeLabel || 'Completed'}</span>
                  </div>
                  {request.myLastActionAt && (
                    <div className="preview-field-sm">
                      <span className="preview-label-sm">You actioned:</span>
                      <span className="preview-value-sm">{formatLongDate(request.myLastActionAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="submission-detail">
            {selectedRequest ? (
              <RequestDetail
                request={selectedRequest}
                user={user}
                onUpdate={loadRequests}
                isApprover={true}
              />
            ) : (
              <div className="detail-empty">
                <div style={{ textAlign: 'center' }}>
                  <ArrowLeft size={48} style={{ marginBottom: '16px' }} />
                  <p style={{ color: '#64748b' }}>Select a request to review</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
